const LOCAL_STORAGE_KEY = 'accordionItems'
const getSavedState = () =>
  _.omitBy(
    JSON.parse(localStorage.getItem(LOCAL_STORAGE_KEY)) || {},
    (value, key) => {
      return moment(value.expires).isBefore(moment())
    },
  )

export const useAccordionItems = defineStore('accordionItems', () => {
  const isInitialized = ref(false)
  const expandedState = ref({})

  if (import.meta.server) {
    return {
      expandedState: {},
      registerAccordion: () => {},
      toggleItem: () => {},
      isItemExpanded: () => {},
      openAccordionItemByDefault: () => {},
    }
  }

  function initialize() {
    if (isInitialized.value) {
      return
    }
    expandedState.value = getSavedState()
    isInitialized.value = true
  }

  function saveState() {
    localStorage.setItem(
      LOCAL_STORAGE_KEY,
      JSON.stringify(
        // do not persist expandedState that have not been registered with an explicit "id"
        // To opt-in to the new persistence behavior, <Accordion id="your-id"> must be used
        _.omitBy(expandedState.value, (value, key) =>
          key.startsWith('accordion-id-'),
        ),
      ),
    )
  }

  function registerAccordion(accordionId, isMultiple = false) {
    initialize()
    if (!expandedState.value[accordionId]) {
      expandedState.value[accordionId] = {
        isMultiple,
        items: {},
        expires: moment().add(1, 'hour').toISOString(),
      }
      saveState()
    }
  }

  function openAccordionItemByDefault(accordionId, itemId) {
    if (expandedState.value?.[accordionId]?.items?.[itemId] === false) {
      // if the item is explicitly closed, do not open it by default
      return
    }
    toggleItem(accordionId, itemId, true)
  }

  function toggleItem(accordionId, itemId, newValue) {
    if (!expandedState.value[accordionId]) {
      return
    }
    newValue = newValue ?? !expandedState.value[accordionId].items[itemId]
    if (expandedState.value[accordionId].isMultiple) {
      expandedState.value[accordionId].items[itemId] = newValue
    } else {
      expandedState.value[accordionId].items = {
        [itemId]: newValue,
      }
    }
    saveState()
  }

  function isItemExpanded(accordionId, itemId) {
    return !!expandedState.value?.[accordionId]?.items?.[itemId]
  }

  return {
    expandedState,
    registerAccordion,
    toggleItem,
    isItemExpanded,
    openAccordionItemByDefault,
  }
})
